import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/AccountDropdown.css';
import LoginModal from './LoginModal';

const AccountDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // "Login / Sign up" in dropdown → opens LoginModal (inline)
  const handleLoginClick = () => {
    setIsOpen(false);
    setShowLoginModal(true);
  };

  // ✅ "Your Account" when not logged in → navigate to /login page (PhoneLogin)
  const handleYourAccountClick = () => {
    setIsOpen(false);
    navigate('/login');
  };

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  const getUserDisplayName = () => {
    if (!user) return 'Account';
    if (user.phone) return `+91 ${user.phone}`;
    if (user.email && !user.email.includes('temp.aucauto.com')) return user.email;
    if (user.name) return user.name;
    return 'Account';
  };

  return (
    <>
      <div
        className="account-dropdown"
        ref={dropdownRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className={`account-btn ${isOpen ? 'active' : ''}`}
          onClick={toggleDropdown}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          <span className="account-icon-wrap">
            <svg className="account-icon-svg" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
              <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <span className="account-label">
            {isAuthenticated ? getUserDisplayName() : 'Account'}
          </span>
          <svg className={`account-chevron ${isOpen ? 'rotated' : ''}`} viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {isOpen && (
          <div className="dropdown-menu" role="menu">
            {isAuthenticated ? (
              <>
                <Link to="/profile" className="dropdown-item" role="menuitem" onClick={() => setIsOpen(false)}>
                  <svg className="item-icon" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  Your Account
                </Link>
                <button className="dropdown-item" role="menuitem" onClick={handleLogout}>
                  <svg className="item-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Logout
                </button>
              </>
            ) : (
              <>
                {/* ✅ "Your Account" → navigates to /login (PhoneLogin page) */}
                <button className="dropdown-item login-item" role="menuitem" onClick={handleYourAccountClick}>
                  <svg className="item-icon" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  Your Account
                </button>

                {/* "Login / Sign up" → opens LoginModal inline */}
                <button className="dropdown-item login-item" role="menuitem" onClick={handleLoginClick}>
                  <svg className="item-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Login / Sign up
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
    </>
  );
};

export default AccountDropdown;